import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../TermsAndConditionsPage/TermsAndConditionsPage.css';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Política de privacidad | Always Aligners';
  }, []);

  return (
    <div className="terms-page">
      <div className="container terms-page__inner">
        <header className="terms-page__header">
          <p className="terms-page__eyebrow">Protección de datos</p>
          <h1 className="terms-page__title">Política de privacidad</h1>
          <p className="terms-page__meta">
            Última actualización: marzo de 2026. Esta política describe cómo Always Aligners trata los
            datos personales en el marco de la legislación de la República Argentina, en particular la
            Ley 25.326 de Protección de Datos Personales y su normativa complementaria.
          </p>
        </header>

        <div className="terms-page__content">
          <section className="terms-page__section" aria-labelledby="priv-s1">
            <h2 id="priv-s1">1. Responsable del tratamiento</h2>
            <p>
              El responsable del tratamiento de los datos personales obtenidos a través del sitio web y
              la plataforma digital <strong>Always Aligners</strong> es quien resulte titular del
              servicio en cada momento, conforme se informe en los canales oficiales o en el pie del
              sitio. Podés solicitar datos identificativos del responsable mediante los medios de
              contacto indicados al final de esta política.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s2">
            <h2 id="priv-s2">2. Alcance</h2>
            <p>
              Esta política aplica a los datos que recabamos cuando navegás el sitio, creás una cuenta,
              comprás cursos u otros productos, participás en formación, te suscribís a comunicaciones o
              interactuás con nuestros formularios y servicios en línea.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s3">
            <h2 id="priv-s3">3. Datos que podemos recopilar</h2>
            <p>Según el servicio utilizado, podemos tratar, entre otros:</p>
            <ul className="privacy-page__list">
              <li>
                <strong>Identificación y contacto:</strong> nombre, apellido, correo electrónico,
                teléfono, país o provincia.
              </li>
              <li>
                <strong>Datos de cuenta:</strong> credenciales de acceso de forma cifrada o resguardada,
                preferencias de perfil. Las operaciones sensibles (como el cambio de contraseña desde la
                cuenta) pueden generar un correo electrónico de aviso a tu dirección registrada cuando el
                servicio de correo esté habilitado.
              </li>
              <li>
                <strong>Datos de formación y uso:</strong> inscripciones a cursos, progreso, evaluaciones
                cuando la plataforma lo registre.
              </li>
              <li>
                <strong>Datos de compra:</strong> identificador de pedido, productos adquiridos, datos
                necesarios para facturación cuando corresponda.
              </li>
              <li>
                <strong>Datos técnicos:</strong> dirección IP, tipo de navegador, dispositivo, cookies o
                tecnologías similares (ver sección 8).
              </li>
            </ul>
            <p>
              No solicitamos datos clínicos de pacientes para el funcionamiento general de la
              plataforma; cualquier información de ese tipo que pudiera intercambiarse en contextos
              formativos específicos se regirá por las reglas y consentimientos propios de dichas
              actividades.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s4">
            <h2 id="priv-s4">4. Finalidades del tratamiento</h2>
            <p>Tratamos los datos para:</p>
            <ul className="privacy-page__list">
              <li>Gestionar el registro, el acceso y la seguridad de tu cuenta.</li>
              <li>
                Ejecutar contratos de compra, habilitar cursos, certificaciones y soporte relacionado.
              </li>
              <li>
                Enviar comunicaciones operativas (confirmaciones, avisos de servicio) y, si lo
                autorizás, novedades formativas o comerciales de Always Aligners.
              </li>
              <li>Cumplir obligaciones legales y responder a requerimientos de autoridades competentes.</li>
              <li>Mejorar la plataforma, analizar uso agregado y prevenir fraudes o abusos.</li>
            </ul>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s5">
            <h2 id="priv-s5">5. Base legal y conservación</h2>
            <p>
              El tratamiento se funda en la ejecución de contratos o medidas precontractuales, el
              cumplimiento de obligaciones legales, el interés legítimo en la mejora y seguridad del
              servicio —cuando corresponda— y, para comunicaciones no esenciales, en tu{' '}
              <strong>consentimiento</strong>, que podés retirar en cualquier momento sin afectar la
              licitud del tratamiento previo.
            </p>
            <p>
              Conservamos los datos el tiempo necesario para las finalidades indicadas y los plazos
              legales o de prescripción aplicables; posteriormente serán suprimidos o anonimizados
              cuando sea posible.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s6">
            <h2 id="priv-s6">6. Cesiones y encargados</h2>
            <p>
              Podemos compartir datos con proveedores que nos prestan servicios de hosting, correo,
              analítica, pasarelas de pago o soporte, bajo acuerdos que obligan a tratar la información
              solo según nuestras instrucciones y con medidas de seguridad adecuadas.
            </p>
            <p>
              No vendemos tus datos personales. Una transferencia internacional de datos podrá
              producirse si el proveedor tiene servidores en el exterior; en tal caso procuramos
              salvaguardas compatibles con la normativa aplicable.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s7">
            <h2 id="priv-s7">7. Derechos de las personas usuarias</h2>
            <p>
              De acuerdo con la Ley 25.326 y normativa complementaria, podés ejercer los derechos de{' '}
              <strong>acceso, rectificación, actualización, supresión</strong> cuando corresponda,{' '}
              <strong>confidencialidad</strong> de los datos y, en su caso, retiro o bloqueo. También
              podés presentar reclamo ante la Dirección Nacional de Protección de Datos Personales u
              organismo que la sustituya.
            </p>
            <p>
              Para ejercer derechos o consultas sobre privacidad, contactanos por los medios indicados
              en la sección 10, acreditando tu identidad de forma razonable.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s8">
            <h2 id="priv-s8">8. Cookies y tecnologías similares</h2>
            <p>
              Utilizamos cookies o tecnologías similares para el funcionamiento del sitio, preferencias
              de sesión y, si está habilitado, estadísticas de uso. Podés configurar tu navegador para
              rechazar cookies, aunque algunas funciones podrían verse limitadas.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s9">
            <h2 id="priv-s9">9. Seguridad</h2>
            <p>
              Aplicamos medidas técnicas y organizativas razonables para proteger los datos frente a
              accesos no autorizados, pérdida o alteración. Ningún sistema es infalible; te recomendamos
              proteger tu contraseña y cerrar sesión en dispositivos compartidos.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s10">
            <h2 id="priv-s10">10. Menores de edad</h2>
            <p>
              Los servicios están dirigidos a personas con capacidad legal para contratar. No recabamos
              datos de menores de forma intencionada; si tomás conocimiento de ello, contactanos para
              proceder a su eliminación.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s11">
            <h2 id="priv-s11">11. Cambios en esta política</h2>
            <p>
              Podemos actualizar este texto para reflejar cambios legales o del servicio. Publicaremos la
              versión vigente en esta página con la fecha de actualización.
            </p>
          </section>

          <section className="terms-page__section" aria-labelledby="priv-s12">
            <h2 id="priv-s12">12. Contacto</h2>
            <p>
              Para consultas sobre esta política o el ejercicio de tus derechos, utilizá los canales que
              Always Aligners disponga en el sitio (correo electrónico o formulario de contacto cuando
              estén disponibles).
            </p>
          </section>
        </div>

        <footer className="terms-page__footer">
          <Link to="/" className="btn btn--secondary terms-page__back">
            Volver al inicio
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
